import './template-styles/ArticlePage.scss'

import React from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { Grid, GridItem } from '@which/seatbelt'

import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react'

import type {
  ArticleJumpLinks,
  ConsumerRightsScamsArticleQuery,
  LatestNews,
} from '../../generated/frontend'
import { useConsumerRightsScamsArticleQuery } from '../../generated/frontend'
import type { PageParams } from '../../routes'
import { useBackStack } from '../../shared/BrowserBackStackProvider'
import { LatestNewsCards } from '../../shared/components//LatestNewsCards/LatestNewsCards'
import { BasicHeader } from '../../shared/components/BasicHeader/BasicHeader'
import { Breadcrumb } from '../../shared/components/Breadcrumb'
import { ErrorComponent, PageError } from '../../shared/components/Error'
import { JumplinksInline } from '../../shared/components/JumplinksInline/JumplinksInline'
import { Loader } from '../../shared/components/Loader'
import { LocalFooter } from '../../shared/components/LocalFooter/LocalFooter'
import { Redirect } from '../../shared/components/Redirect'
import { ResubscribeBanner } from '../../shared/components/ResubscribeBanner/ResubscribeBanner'
import { getDataLayerScripts } from '../../shared/data-layer'
import { getDFPScripts } from '../../shared/dfp'
import { useBaseMetaTags } from '../../shared/hooks/useBaseMetaTags'
import { useFullUrl } from '../../shared/hooks/useFullUrl'
import { articleBodyRenderer } from '../../shared/renderers/article-body-renderer'
import type { PageInfo } from '../../shared/types/PageInfo'
import { usePageProps } from '../../shared/usePageProps'
import { getPreviousUrl } from '../../shared/utils/getPreviousUrl/getPreviousUrl'
import { removeFalsy } from '../../shared/utils/remove-falsy'
import { PageWrapperWithMeta } from '../../shared/wrappers/PageWrapperWithMeta'
import { ToastNotification } from '../reviews/shared/components//ToastNotification/ToastNotification'
import { ArticleTemplateProvider } from './ArticleTemplateProvider'
import { Save } from './components/Save'
import { WhichConnectMessage } from './components/WhichConnectMessage/WhichConnectMessage'
import type { SquirrelScriptsData } from './hooks/use-squirrel-script'
import { useSquirrelScript } from './hooks/use-squirrel-script'
import styles from './template-styles/ConsumerRightsScams.module.scss'
import { baseDataLayer } from './utils/base-data-layer'
import { renderResubscribeBanner } from './utils/render-resubscribe-banner'
import { generateSchema } from './utils/schema'

const ConsumeRightsScamsPage: React.FC = () => {
  const { pathname, search } = useLocation()
  const { context = '', userAccessState } = usePageProps()
  const { loading, error, data } = useConsumerRightsScamsArticleQuery({
    variables: { url: `${pathname}${search}`, context },
  })

  const { getFullUrl } = useFullUrl()
  const backStack = useBackStack()
  const previousUrl = getPreviousUrl({ backStack })

  const { getBaseMetaTags } = useBaseMetaTags()
  const { articleName } = useParams<PageParams>()

  useSquirrelScript(data as SquirrelScriptsData)

  const resubscribeBannerFlag = useFeatureIsOn('resubscribe-banner')
  const gbTestValue = useFeatureValue('ctx-011-contextualised-newsletter', '')

  const isLoggedIn = userAccessState?.userLoggedIn ?? false
  const transformTypeDecision = userAccessState?.transformTypeDecision ?? ''

  if (loading) {
    return <Loader />
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  if (!data?.articlePage) {
    return <PageError pageName="Article Page" />
  }

  const { articlePage } = data

  if (articlePage.__typename === 'Redirect') {
    return <Redirect to={articlePage.redirectUrl} />
  }

  const {
    articleBodyTemplate,
    articleConfig,
    breadcrumb,
    header,
    jumpLinks,
    latestNews,
    meta,
    consumerRightsFooter,
    showResubBanner,
    savedStatus,
  } = articlePage

  const baseMetaTags = getBaseMetaTags({
    meta,
    context,
    twitterCard: 'summary_large_image',
  })
  const metaTags = getMetaTags({
    articlePage,
    baseMetaTags,
    urls: { currentUrl: getFullUrl(), previousUrl },
    gbTestValue,
    articleName,
  })

  return (
    <ArticleTemplateProvider meta={meta} initialSavedStatus={savedStatus}>
      <div data-testid="template-portal" data-template-type="consumer-rights-scams-article">
        {renderResubscribeBanner(resubscribeBannerFlag, transformTypeDecision) && (
          <ResubscribeBanner showBanner={showResubBanner} />
        )}
        <Breadcrumb {...breadcrumb} />
        <PageWrapperWithMeta metaTags={metaTags}>
          <Grid>
            <GridItem span={{ medium: 10, large: 10 }} columnStart={{ medium: 2, large: 2 }}>
              <BasicHeader header={header.headline} standfirst={header.standfirst} />
            </GridItem>
            {isLoggedIn && (
              <GridItem span={{ medium: 10, large: 10 }} columnStart={{ medium: 2, large: 2 }}>
                <Save location="hero" />
              </GridItem>
            )}
            <GridItem span={{ medium: 10, large: 10 }} columnStart={{ medium: 2, large: 2 }}>
              <JumplinksInline links={mapJumplinks(jumpLinks)} />
            </GridItem>
            <GridItem
              className={styles.scamsArticle}
              span={{ medium: 10, large: 10 }}
              columnStart={{ medium: 2, large: 2 }}
            >
              {articleBodyRenderer(articleBodyTemplate)}
            </GridItem>
            {isLoggedIn && (
              <GridItem span={{ medium: 10, large: 10 }} columnStart={{ medium: 2, large: 2 }}>
                <Save location="content" />
              </GridItem>
            )}
            <GridItem span={{ medium: 10, large: 10 }} columnStart={{ medium: 2, large: 2 }}>
              <WhichConnectMessage showMessage={articleConfig?.whichConnectMessage} />
            </GridItem>
            <GridItem span={{ small: 'full-bleed', medium: 'full-bleed', large: 'full-bleed' }}>
              <LatestNewsCards
                name="Latest scam news"
                articles={latestNews?.articles as LatestNews['articles']}
                viewAllData={{ text: 'View all news', href: '/news/tag/scams' }}
              />
            </GridItem>
            <GridItem>
              <LocalFooter data={consumerRightsFooter} />
            </GridItem>
          </Grid>
        </PageWrapperWithMeta>
      </div>
      <ToastNotification />
    </ArticleTemplateProvider>
  )
}

export default ConsumeRightsScamsPage

const mapJumplinks = (jumpLinks: ArticleJumpLinks[]) =>
  jumpLinks.map(({ name, slug }) => ({
    id: slug,
    label: name,
  }))

export type ConsumerRightsScamsArticleData = ConsumerRightsScamsArticleQuery['articlePage'] & {
  __typename: 'ArticlePage'
}

const getMetaTags = ({
  articlePage: { meta, articleBodyTemplate },
  baseMetaTags,
  urls,
  gbTestValue,
  articleName,
}: {
  articlePage: ConsumerRightsScamsArticleData
  baseMetaTags: JSX.Element[]
  urls: { currentUrl: string; previousUrl?: string }
  gbTestValue: string
  articleName: string
}) => {
  const keywords = meta.keywords.length
    ? [<meta key="keywords" name="keywords" content={meta.keywords.join(', ')} />]
    : []

  const basePageInfo: PageInfo = {
    pageType: 'article',
    vertical: 'consumer-rights',
    content_id: meta.contentId,
    isVideoArticle: false,
  }

  // data layer
  const dataLayerPageInfo: DataLayer = {
    ...basePageInfo,
    ...baseDataLayer(meta, urls, articleBodyTemplate),
    keywords: meta.keywords?.length ? meta.keywords.join(' | ') : '',
    tags: meta.keywords?.length ? meta.keywords.join(' | ') : '',
    topicName: 'scams',
    subtopicName: articleName,
    content_type: 'subtopic',
  }
  const dataLayer = getDataLayerScripts([...(meta.dataLayer || []), removeFalsy(dataLayerPageInfo)])

  // doubleclick for publishers
  const dfpMeta = getDFPScripts(meta.dataLayer, {
    ...basePageInfo,
    sub_vertical: 'scams',
    category: meta.taxonomyHierarchy?.category?.slug,
    keywords: meta.keywords,
    tags: meta.keywords,
    GB_test_value: gbTestValue,
  })

  // schema for SEO
  const schema = generateSchema({ meta })

  return baseMetaTags.concat(
    keywords,
    dataLayer,
    dfpMeta,
    schema,
    <link rel="preconnect" href="https://securepubads.g.doubleclick.net" />
  )
}

type DataLayer = PageInfo & {
  topicName?: string
  subtopicName?: string
}
